import React, { useState } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, FontStacks, BorderRadius, Shadows, Layout } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import PageContainer from '../components/layout/PageContainer';
import AzureEntraIdDashboard from '../components/admin/AzureEntraIdDashboard';
import PageActions from '../components/PageActions';
import { 
  mockSystemUsers, 
  mockAuditLogs, 
  mockSystemConfigurations,
  mockDataIntegrations,
  mockSecurityPolicies,
  mockSystemHealth,
  mockBackupConfigurations,
  mockComplianceReports
} from '../utils/mockAdminData';

const ContentWrapper = styled.div`
  padding-bottom: ${Spacing.xl}px;
`;

const TabNavigation = styled.div`
  position: sticky;
  top: ${Layout.headerHeightPx}; /* Position below the main header */
  z-index: ${Layout.zIndex.content + 10}; /* Above content but below main header */
  background: ${Colors.surface};
  border-bottom: 1px solid ${Colors.border};
  margin: 0 -${Spacing.xl}px ${Spacing.xl * 1.5}px;
  padding: 0 ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    margin: 0 -${Spacing.md}px ${Spacing.lg}px;
    padding: 0 ${Spacing.md}px;
  }
`;

const TabScrollContainer = styled.div`
  display: flex;
  gap: 0;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${Colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${Colors.border};
    border-radius: 2px;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? Colors.primary : Colors.textSecondary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 200ms ease;
  white-space: nowrap;
  position: relative;
  
  &:hover {
    color: ${Colors.primary};
    background-color: ${Colors.surfaceHover};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.primary}30;
    border-radius: ${BorderRadius.sm}px;
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.md}px ${Spacing.lg}px;
    font-size: ${FontSizes.sm}px;
  }
`;

const TabContent = styled.div`
  animation: fadeIn 300ms ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  /* Global vertical rhythm */
  > * {
    margin-bottom: ${Spacing.xl * 1.5}px;
  }
  
  > *:last-child {
    margin-bottom: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl * 1.5}px;
`;

const StatCard = styled.div`
  background-color: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.xs};
  transition: all 200ms ease;
  
  &:hover {
    border-color: ${Colors.border};
    box-shadow: ${Shadows.sm};
    transform: translateY(-1px);
  }
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xs}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${FontSizes.xxxl}px;
  font-weight: ${FontWeights.bold};
  color: ${props => props.color || Colors.textPrimary};
`;

const StatChange = styled.div<{ positive?: boolean }>`
  font-size: ${FontSizes.sm}px;
  color: ${props => props.positive ? Colors.success : Colors.textSecondary};
  margin-top: ${Spacing.xs}px;
`;

const TableContainer = styled.div`
  background-color: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.xs};
  overflow: hidden;
  margin-bottom: ${Spacing.xl * 1.5}px;
`;

const TableHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${Spacing.sm}px;
  }
`;

const StandardActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  border-radius: ${BorderRadius.sm}px;
  cursor: pointer;
  transition: all 200ms ease;
  border: 1px solid transparent;
  white-space: nowrap;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${Colors.primary};
          color: ${Colors.textOnPrimary};
          border-color: ${Colors.primary};
          
          &:hover {
            background: ${Colors.primaryDark};
            border-color: ${Colors.primaryDark};
            transform: translateY(-1px);
            box-shadow: ${Shadows.sm};
          }
        `;
      
      case 'danger':
        return `
          background: ${Colors.danger};
          color: ${Colors.textOnPrimary};
          border-color: ${Colors.danger};
          
          &:hover {
            opacity: 0.9;
            transform: translateY(-1px);
            box-shadow: ${Shadows.sm};
          }
        `;
      
      case 'secondary':
      default:
        return `
          background: ${Colors.surface};
          color: ${Colors.textPrimary};
          border-color: ${Colors.border};
          
          &:hover {
            background: ${Colors.surfaceHover};
            border-color: ${Colors.primary};
            box-shadow: ${Shadows.sm};
          }
        `;
    }
  }}
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.primary}30;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: linear-gradient(to bottom, ${Colors.background}, ${Colors.backgroundDark});
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${Colors.borderLight};
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
  }
  
  &:nth-child(even) {
    background-color: ${Colors.background};
    
    &:hover {
      background-color: ${Colors.surfaceHover};
    }
  }
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.lg}px ${Spacing.md}px;
  text-align: left;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid ${Colors.border};
  white-space: nowrap;
  background: ${Colors.background};
`;

const TableCell = styled.td`
  padding: ${Spacing.md}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  vertical-align: middle;
  
  &:first-child {
    font-weight: ${FontWeights.medium};
  }
`;

const StatusBadge = styled.span<{ status: 'ACTIVE' | 'INACTIVE' | 'ERROR' | 'SUCCESS' | 'FAILED' | 'WARNING' | 'SUSPENDED' | 'PENDING' | 'SYNCING' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
  border: 1px solid transparent;
  
  ${props => {
    switch (props.status) {
      case 'ACTIVE':
      case 'SUCCESS': 
        return `
          background: ${Colors.success}20;
          color: ${Colors.success};
          border-color: ${Colors.success}30;
        `;
      case 'INACTIVE': 
        return `
          background: ${Colors.textTertiary}20;
          color: ${Colors.textTertiary};
          border-color: ${Colors.textTertiary}30;
        `;
      case 'ERROR':
      case 'FAILED': 
        return `
          background: ${Colors.danger}20;
          color: ${Colors.danger};
          border-color: ${Colors.danger}30;
        `;
      case 'WARNING':
      case 'SUSPENDED': 
        return `
          background: ${Colors.warning}20;
          color: ${Colors.warning};
          border-color: ${Colors.warning}30;
        `;
      case 'PENDING':
      case 'SYNCING': 
        return `
          background: ${Colors.info}20;
          color: ${Colors.info};
          border-color: ${Colors.info}30;
        `;
      default: 
        return `
          background: ${Colors.borderLight};
          color: ${Colors.textSecondary};
        `;
    }
  }}
`;

const HealthIndicator = styled.div<{ health: 'HEALTHY' | 'DEGRADED' | 'ERROR' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.md}px;
  background-color: ${props => {
    switch (props.health) {
      case 'HEALTHY': return Colors.success + '20';
      case 'DEGRADED': return Colors.warning + '20';
      case 'ERROR': return Colors.danger + '20';
    }
  }};
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.lg}px;
`;

const HealthDot = styled.div<{ status: 'UP' | 'DOWN' | 'DEGRADED' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'UP': return Colors.success;
      case 'DEGRADED': return Colors.warning;
      case 'DOWN': return Colors.danger;
    }
  }};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${Colors.backgroundDark};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number; color?: string }>`
  height: 100%;
  width: ${props => props.percent}%;
  background-color: ${props => props.color || Colors.primary};
  transition: width 300ms ease;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.lg}px;
`;

const ServiceCard = styled.div`
  padding: ${Spacing.md}px;
  background-color: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchBar = styled.input`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background-color: ${Colors.background};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  min-width: 250px;
  transition: all 200ms ease;
  
  &::placeholder {
    color: ${Colors.textTertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 2px ${Colors.primary}20;
  }
  
  @media (max-width: 768px) {
    min-width: 200px;
  }
`;

const ConfigGrid = styled.div`
  display: grid;
  gap: ${Spacing.md}px;
`;

const ConfigItem = styled.div`
  padding: ${Spacing.lg}px;
  background-color: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: ${Spacing.lg}px;
`;

const ConfigInfo = styled.div``;

const ConfigName = styled.div`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const ConfigDescription = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const ConfigValue = styled.div`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.primary};
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${Colors.borderDark};
    transition: .4s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: ${Colors.success};
  }
  
  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const ComplianceCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
`;

const ComplianceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg}px;
`;

const ComplianceScore = styled.div<{ score: number }>`
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${props => 
    props.score >= 90 ? Colors.success :
    props.score >= 70 ? Colors.warning :
    Colors.danger
  };
`;

// Critical status panel for sync failures and other issues
const CriticalStatusPanel = styled.div`
  background: ${Colors.danger}08;
  border: 1px solid ${Colors.danger}30;
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl * 1.5}px;
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.md}px;
`;

const CriticalIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${Colors.danger};
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.lg}px;
  flex-shrink: 0;
`;

const CriticalContent = styled.div`
  flex: 1;
`;

const CriticalTitle = styled.div`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.danger};
  margin-bottom: ${Spacing.xs}px;
`;

const CriticalDescription = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.md}px;
  line-height: 1.5;
`;

const CriticalActions = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  flex-wrap: wrap;
`;

const CriticalActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => props.variant === 'primary' ? Colors.danger : Colors.surface};
  color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.variant === 'primary' ? Colors.danger : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const AdministrationScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'identity' | 'users' | 'system' | 'integrations' | 'security' | 'audit' | 'backup' | 'compliance'>('identity');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = mockSystemUsers.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLogs = mockAuditLogs.filter(log =>
    log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.userName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeIntegrations = mockDataIntegrations.filter(i => i.status === 'ACTIVE').length;
  const totalUsers = mockSystemUsers.length;
  const activeUsers = mockSystemUsers.filter(u => u.status === 'ACTIVE').length;
  const failedLogins = mockAuditLogs.filter(l => l.status === 'FAILED').length;
  
  // Check for critical integration issues
  const criticalIntegrations = mockDataIntegrations.filter(integration => {
    const lastSyncDate = new Date(integration.lastSync);
    const daysSinceSync = Math.floor((Date.now() - lastSyncDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceSync > 30 || integration.status === 'ERROR';
  });
  
  const handleRetrySync = (integrationId: string) => {
    console.log('Retrying sync for integration:', integrationId);
    // Implementation for retry sync
  };
  
  const handleOpenIntegrationSettings = () => {
    setActiveTab('integrations');
  };
  
  const handleViewLogs = (integrationId: string) => {
    console.log('Viewing logs for integration:', integrationId);
    // Implementation for viewing logs
  };

  return (
    <PageContainer>
      <UnifiedPageHeader
        title="Administration"
        subtitle="System configuration, user management, and platform settings"
        metadata={{
          timestamp: new Date(),
          count: totalUsers,
          status: 'Live'
        }}
        actions={[
          {
            label: 'Refresh',
            onClick: () => window.location.reload(),
            variant: 'ghost' as const,
            icon: '🔄'
          },
          {
            label: 'Export',
            onClick: () => console.log('Export data'),
            variant: 'secondary' as const,
            icon: '📤'
          },
          {
            label: 'Settings',
            onClick: () => console.log('Open settings'),
            variant: 'primary' as const,
            icon: '⚙️'
          }
        ]}
      />

      <ContentWrapper>
        {/* Critical Status Panels */}
        {criticalIntegrations.map(integration => {
          const lastSyncDate = new Date(integration.lastSync);
          const daysSinceSync = Math.floor((Date.now() - lastSyncDate.getTime()) / (1000 * 60 * 60 * 24));
          
          return (
            <CriticalStatusPanel key={integration.id}>
              <CriticalIcon>⚠️</CriticalIcon>
              <CriticalContent>
                <CriticalTitle>Sync Failed</CriticalTitle>
                <CriticalDescription>
                  <strong>{integration.name}</strong> integration has failed. 
                  Last successful sync: <strong>{daysSinceSync} days ago</strong>
                  {integration.errorCount > 0 && ` with ${integration.errorCount} errors`}.
                </CriticalDescription>
                <CriticalActions>
                  <CriticalActionButton 
                    variant="primary"
                    onClick={() => handleRetrySync(integration.id)}
                  >
                    Retry Now
                  </CriticalActionButton>
                  <CriticalActionButton onClick={handleOpenIntegrationSettings}>
                    Open Integration Settings
                  </CriticalActionButton>
                  <CriticalActionButton onClick={() => handleViewLogs(integration.id)}>
                    View Logs
                  </CriticalActionButton>
                </CriticalActions>
              </CriticalContent>
            </CriticalStatusPanel>
          );
        })}

      <StatsGrid>
        <StatCard>
          <StatLabel>System Health</StatLabel>
          <StatValue color={Colors.success}>{mockSystemHealth.status}</StatValue>
          <StatChange positive>Uptime: {Math.floor(mockSystemHealth.uptime / 86400)} days</StatChange>
        </StatCard>
        <StatCard>
          <StatLabel>Active Users</StatLabel>
          <StatValue>{activeUsers}/{totalUsers}</StatValue>
          <StatChange>{mockSystemHealth.activeUsers} online now</StatChange>
        </StatCard>
        <StatCard>
          <StatLabel>Integrations</StatLabel>
          <StatValue>{activeIntegrations}/{mockDataIntegrations.length}</StatValue>
          <StatChange positive={activeIntegrations === mockDataIntegrations.length}>
            {mockDataIntegrations.length - activeIntegrations} need attention
          </StatChange>
        </StatCard>
        <StatCard>
          <StatLabel>Security Events</StatLabel>
          <StatValue color={failedLogins > 5 ? Colors.warning : Colors.textPrimary}>
            {failedLogins}
          </StatValue>
          <StatChange>Failed logins (24h)</StatChange>
        </StatCard>
      </StatsGrid>

      <TabNavigation>
        <TabScrollContainer>
          <TabButton active={activeTab === 'identity'} onClick={() => setActiveTab('identity')}>
            🔐 Azure Entra ID
          </TabButton>
          <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            User Management
          </TabButton>
          <TabButton active={activeTab === 'system'} onClick={() => setActiveTab('system')}>
            System Health
          </TabButton>
          <TabButton active={activeTab === 'integrations'} onClick={() => setActiveTab('integrations')}>
            Integrations
          </TabButton>
          <TabButton active={activeTab === 'security'} onClick={() => setActiveTab('security')}>
            Security Policies
          </TabButton>
          <TabButton active={activeTab === 'audit'} onClick={() => setActiveTab('audit')}>
            Audit Logs
          </TabButton>
          <TabButton active={activeTab === 'backup'} onClick={() => setActiveTab('backup')}>
            Backup & Recovery
          </TabButton>
          <TabButton active={activeTab === 'compliance'} onClick={() => setActiveTab('compliance')}>
            Compliance
          </TabButton>
        </TabScrollContainer>
      </TabNavigation>

      <TabContent>
        {activeTab === 'identity' && (
          <AzureEntraIdDashboard />
        )}
        
        {activeTab === 'users' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>System Users</TableTitle>
              <TableActions>
                <SearchBar
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <StandardActionButton variant="primary">+ Add User</StandardActionButton>
              </TableActions>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Department</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>MFA</TableHeaderCell>
                  <TableHeaderCell>Last Login</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role.replace('_', ' ')}</TableCell>
                    <TableCell>{user.department}</TableCell>
                    <TableCell>
                      <StatusBadge status={user.status}>{user.status}</StatusBadge>
                    </TableCell>
                    <TableCell>{user.mfaEnabled ? '✓' : '✗'}</TableCell>
                    <TableCell>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <TableActions>
                        <StandardActionButton>Edit</StandardActionButton>
                        <StandardActionButton variant="danger">Suspend</StandardActionButton>
                      </TableActions>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'system' && (
          <>
            <HealthIndicator health={mockSystemHealth.status}>
              <div style={{ fontSize: `${FontSizes.lg}px`, fontWeight: FontWeights.semibold }}>
                System Status: {mockSystemHealth.status}
              </div>
            </HealthIndicator>

            <StatsGrid>
              <StatCard>
                <StatLabel>CPU Usage</StatLabel>
                <StatValue>{mockSystemHealth.cpu}%</StatValue>
                <ProgressBar>
                  <ProgressFill 
                    percent={mockSystemHealth.cpu} 
                    color={mockSystemHealth.cpu > 80 ? Colors.danger : Colors.primary}
                  />
                </ProgressBar>
              </StatCard>
              <StatCard>
                <StatLabel>Memory Usage</StatLabel>
                <StatValue>{mockSystemHealth.memory}%</StatValue>
                <ProgressBar>
                  <ProgressFill 
                    percent={mockSystemHealth.memory}
                    color={mockSystemHealth.memory > 80 ? Colors.warning : Colors.primary}
                  />
                </ProgressBar>
              </StatCard>
              <StatCard>
                <StatLabel>Disk Usage</StatLabel>
                <StatValue>{mockSystemHealth.disk}%</StatValue>
                <ProgressBar>
                  <ProgressFill 
                    percent={mockSystemHealth.disk}
                    color={mockSystemHealth.disk > 80 ? Colors.warning : Colors.primary}
                  />
                </ProgressBar>
              </StatCard>
              <StatCard>
                <StatLabel>API Response Time</StatLabel>
                <StatValue>{mockSystemHealth.responseTime}ms</StatValue>
                <StatChange positive={mockSystemHealth.responseTime < 500}>
                  {mockSystemHealth.apiCalls.toLocaleString()} calls today
                </StatChange>
              </StatCard>
            </StatsGrid>

            <TableContainer>
              <TableHeader>
                <TableTitle>Service Status</TableTitle>
              </TableHeader>
              <div style={{ padding: `${Spacing.lg}px` }}>
                <ServiceGrid>
                  {mockSystemHealth.services.map(service => (
                    <ServiceCard key={service.name}>
                      <div>
                        <div style={{ fontWeight: FontWeights.medium }}>{service.name}</div>
                        <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary }}>
                          {service.latency}ms
                        </div>
                      </div>
                      <HealthDot status={service.status} />
                    </ServiceCard>
                  ))}
                </ServiceGrid>
              </div>
            </TableContainer>
          </>
        )}

        {activeTab === 'integrations' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Data Integrations</TableTitle>
              <StandardActionButton variant="primary">+ Add Integration</StandardActionButton>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Last Sync</TableHeaderCell>
                  <TableHeaderCell>Frequency</TableHeaderCell>
                  <TableHeaderCell>Records</TableHeaderCell>
                  <TableHeaderCell>Errors</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {mockDataIntegrations.map(integration => (
                  <TableRow key={integration.id}>
                    <TableCell>{integration.name}</TableCell>
                    <TableCell>{integration.type}</TableCell>
                    <TableCell>
                      <StatusBadge status={integration.status}>{integration.status}</StatusBadge>
                    </TableCell>
                    <TableCell>
                      {integration.lastSync ? (() => {
                        const lastSyncDate = new Date(integration.lastSync);
                        const daysSinceSync = Math.floor((Date.now() - lastSyncDate.getTime()) / (1000 * 60 * 60 * 24));
                        
                        if (daysSinceSync > 30) {
                          return (
                            <span style={{ color: Colors.danger, fontWeight: FontWeights.semibold }}>
                              {daysSinceSync}d ago (CRITICAL)
                            </span>
                          );
                        } else if (daysSinceSync > 7) {
                          return (
                            <span style={{ color: Colors.warning, fontWeight: FontWeights.medium }}>
                              {daysSinceSync}d ago
                            </span>
                          );
                        } else {
                          return lastSyncDate.toLocaleString();
                        }
                      })() : 'Never'}
                    </TableCell>
                    <TableCell>{integration.syncFrequency}</TableCell>
                    <TableCell>{integration.recordsProcessed.toLocaleString()}</TableCell>
                    <TableCell>
                      <span style={{ color: integration.errorCount > 0 ? Colors.danger : Colors.textSecondary }}>
                        {integration.errorCount}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TableActions>
                        <StandardActionButton>Configure</StandardActionButton>
                        <StandardActionButton variant="primary">Sync Now</StandardActionButton>
                      </TableActions>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'security' && (
          <ConfigGrid>
            {mockSecurityPolicies.map(policy => (
              <TableContainer key={policy.id}>
                <TableHeader>
                  <TableTitle>{policy.name}</TableTitle>
                  <ToggleSwitch>
                    <input type="checkbox" checked={policy.enabled} readOnly />
                    <span></span>
                  </ToggleSwitch>
                </TableHeader>
                <div style={{ padding: `${Spacing.lg}px` }}>
                  <div style={{ marginBottom: `${Spacing.md}px`, color: Colors.textSecondary }}>
                    Applies to: {Array.isArray(policy.appliesTo) ? policy.appliesTo.join(', ') : policy.appliesTo}
                  </div>
                  {policy.rules.map((rule, index) => (
                    <ConfigItem key={index}>
                      <ConfigInfo>
                        <ConfigName>{rule.description}</ConfigName>
                      </ConfigInfo>
                      <ConfigValue>
                        {typeof rule.value === 'boolean' ? (rule.value ? '✓' : '✗') : 
                         Array.isArray(rule.value) ? rule.value.join(', ') : rule.value}
                      </ConfigValue>
                    </ConfigItem>
                  ))}
                  <div style={{ marginTop: `${Spacing.md}px`, fontSize: `${FontSizes.xs}px`, color: Colors.textTertiary }}>
                    Last updated: {new Date(policy.lastUpdated).toLocaleString()} by {policy.updatedBy}
                  </div>
                </div>
              </TableContainer>
            ))}
          </ConfigGrid>
        )}

        {activeTab === 'audit' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Audit Logs</TableTitle>
              <TableActions>
                <SearchBar
                  type="text"
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <StandardActionButton>Export</StandardActionButton>
              </TableActions>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Timestamp</TableHeaderCell>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Category</TableHeaderCell>
                  <TableHeaderCell>Details</TableHeaderCell>
                  <TableHeaderCell>IP Address</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                    <TableCell>{log.userName}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.category}</TableCell>
                    <TableCell>{log.details}</TableCell>
                    <TableCell>{log.ipAddress}</TableCell>
                    <TableCell>
                      <StatusBadge status={log.status}>{log.status}</StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'backup' && (
          <>
            {mockBackupConfigurations.map(backup => (
              <TableContainer key={backup.id} style={{ marginBottom: `${Spacing.lg}px` }}>
                <TableHeader>
                  <TableTitle>{backup.name}</TableTitle>
                  <StatusBadge status={backup.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'}>
                    {backup.status}
                  </StatusBadge>
                </TableHeader>
                <div style={{ padding: `${Spacing.lg}px` }}>
                  <ConfigGrid>
                    <ConfigItem>
                      <ConfigInfo>
                        <ConfigName>Type</ConfigName>
                        <ConfigDescription>Backup type and schedule</ConfigDescription>
                      </ConfigInfo>
                      <ConfigValue>{backup.type} - {backup.schedule}</ConfigValue>
                    </ConfigItem>
                    <ConfigItem>
                      <ConfigInfo>
                        <ConfigName>Destination</ConfigName>
                        <ConfigDescription>Storage location</ConfigDescription>
                      </ConfigInfo>
                      <ConfigValue style={{ fontSize: `${FontSizes.sm}px` }}>
                        {backup.destination}
                      </ConfigValue>
                    </ConfigItem>
                    <ConfigItem>
                      <ConfigInfo>
                        <ConfigName>Retention</ConfigName>
                        <ConfigDescription>Data retention period</ConfigDescription>
                      </ConfigInfo>
                      <ConfigValue>{backup.retention} days</ConfigValue>
                    </ConfigItem>
                    <ConfigItem>
                      <ConfigInfo>
                        <ConfigName>Last Backup</ConfigName>
                        <ConfigDescription>Most recent successful backup</ConfigDescription>
                      </ConfigInfo>
                      <ConfigValue style={{ fontSize: `${FontSizes.sm}px` }}>
                        {new Date(backup.lastBackup).toLocaleString()}
                      </ConfigValue>
                    </ConfigItem>
                    <ConfigItem>
                      <ConfigInfo>
                        <ConfigName>Next Backup</ConfigName>
                        <ConfigDescription>Scheduled next backup</ConfigDescription>
                      </ConfigInfo>
                      <ConfigValue style={{ fontSize: `${FontSizes.sm}px` }}>
                        {new Date(backup.nextBackup).toLocaleString()}
                      </ConfigValue>
                    </ConfigItem>
                  </ConfigGrid>
                  <div style={{ marginTop: `${Spacing.lg}px`, display: 'flex', gap: `${Spacing.md}px` }}>
                    <StandardActionButton variant="primary">Run Now</StandardActionButton>
                    <StandardActionButton>Configure</StandardActionButton>
                    <StandardActionButton>View History</StandardActionButton>
                  </div>
                </div>
              </TableContainer>
            ))}
          </>
        )}

        {activeTab === 'compliance' && (
          <div style={{ display: 'grid', gap: `${Spacing.lg}px` }}>
            {mockComplianceReports.map(report => (
              <ComplianceCard key={report.id}>
                <ComplianceHeader>
                  <div>
                    <div style={{ fontSize: `${FontSizes.xl}px`, fontWeight: FontWeights.semibold }}>
                      {report.type} Compliance
                    </div>
                    <StatusBadge status={report.status === 'COMPLIANT' ? 'SUCCESS' : 'WARNING'}>
                      {report.status.replace('_', ' ')}
                    </StatusBadge>
                  </div>
                  <ComplianceScore score={report.score}>
                    {report.score}%
                  </ComplianceScore>
                </ComplianceHeader>
                
                <div style={{ marginBottom: `${Spacing.lg}px` }}>
                  <ProgressBar>
                    <ProgressFill 
                      percent={report.score}
                      color={report.score >= 90 ? Colors.success : 
                             report.score >= 70 ? Colors.warning : Colors.danger}
                    />
                  </ProgressBar>
                </div>
                
                <ConfigGrid>
                  <ConfigItem>
                    <ConfigInfo>
                      <ConfigName>Last Audit</ConfigName>
                    </ConfigInfo>
                    <ConfigValue style={{ fontSize: `${FontSizes.sm}px` }}>
                      {new Date(report.lastAudit).toLocaleDateString()}
                    </ConfigValue>
                  </ConfigItem>
                  <ConfigItem>
                    <ConfigInfo>
                      <ConfigName>Next Audit</ConfigName>
                    </ConfigInfo>
                    <ConfigValue style={{ fontSize: `${FontSizes.sm}px` }}>
                      {new Date(report.nextAudit).toLocaleDateString()}
                    </ConfigValue>
                  </ConfigItem>
                </ConfigGrid>
                
                {report.findings.length > 0 && (
                  <div style={{ marginTop: `${Spacing.lg}px` }}>
                    <div style={{ 
                      fontSize: `${FontSizes.sm}px`, 
                      fontWeight: FontWeights.semibold,
                      marginBottom: `${Spacing.md}px`
                    }}>
                      Findings ({report.findings.length})
                    </div>
                    {report.findings.map((finding, index) => (
                      <div key={index} style={{
                        padding: `${Spacing.md}px`,
                        backgroundColor: Colors.backgroundDark,
                        borderRadius: `${BorderRadius.md}px`,
                        marginBottom: `${Spacing.sm}px`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: `${Spacing.xs}px` }}>
                          <StatusBadge 
                            status={finding.severity === 'CRITICAL' || finding.severity === 'HIGH' ? 'ERROR' : 'WARNING'}
                          >
                            {finding.severity}
                          </StatusBadge>
                          <StatusBadge status={finding.status === 'RESOLVED' ? 'SUCCESS' : 'WARNING'}>
                            {finding.status}
                          </StatusBadge>
                        </div>
                        <div style={{ fontSize: `${FontSizes.sm}px`, marginBottom: `${Spacing.xs}px` }}>
                          {finding.description}
                        </div>
                        <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary }}>
                          Recommendation: {finding.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div style={{ marginTop: `${Spacing.lg}px`, display: 'flex', gap: `${Spacing.md}px` }}>
                  <StandardActionButton variant="primary">View Full Report</StandardActionButton>
                  <StandardActionButton>Schedule Audit</StandardActionButton>
                </div>
              </ComplianceCard>
            ))}
          </div>
        )}
      </TabContent>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AdministrationScreen;